<template lang="pug">
.study-question-vertical(:style='setStyle')
  //- タイトル部
  .study-question-vertical__title
    .study-question-vertical__title__text.font-bold.text-large
      .study-question-vertical__title__text__problem-number
        .study-question-vertical__title__text__problem-number__label
          slot(name='problemNumberBeforeLabel')
        .study-question-vertical__title__text__problem-number__value {{ problemNumber }}
        .study-question-vertical__title__text__problem-number__label
          slot(name='problemNumberAfterLabel')
      .study-question-vertical__title__text__question-number {{ questionNumberText }}
    pen-selector.study-question-vertical__title__pen-selector(
      ref='penSelector',
      :isVertical='true',
      @updated='changeActivePen'
    )
  //- 音声プレイヤー部
  .study-question-vertical__audio(v-if='audioUrl')
    image-base.study-question-vertical__audio__comment-image(:src='audioCommentImageUrl')
    audio-player.study-question-vertical__audio__player(
      :src='audioUrl',
      :playCount='audioPlayCount',
      :showIndicator='audioViewSetting.showIndicator',
      :availablePause='audioViewSetting.availablePause',
      :availableReplay='audioViewSetting.availableReplay',
      @started='audioStartedEvent'
    )
  //- 問題表示部
  .study-question-vertical__content(v-if='!isChangingProblem')
    .study-question-vertical__content__question(ref='questionBlock')
      image-base.study-question-vertical__content__question__image(
        v-for='(imageUrl, index) in imageUrls',
        :src='imageUrl',
        :key='index',
        @load='adjustAndRestoreCanvas'
      )
      pen-canvas.study-question-vertical__content__question__canvas(
        ref='penCanvas',
        :pen='activePen',
        mode='vertical',
        :drawingData='questionDrawingData',
        @draw-end='drawEndEvent'
      )
</template>

<script lang="ts">
import { Component, Prop, Vue, Ref, Watch } from 'vue-property-decorator'
import PenSelector from '@/components/organisms/student/PenSelector.vue'
import ImageBase from '@/components/atoms/ImageBase.vue'
import AudioPlayer from '@/components/molecules/student/AudioPlayer.vue'
import PenCanvas from '@/components/molecules/student/PenCanvas.vue'
import { DrawSettings } from '@/models/drawSettings'
import { DrawingDataType } from '@/models/drawingDataModel'
import { AudioViewSetting } from '@/const/AudioViewTypes'
import { throttle, debounce } from 'lodash'

@Component({
  components: {
    PenSelector,
    ImageBase,
    AudioPlayer,
    PenCanvas,
  },
})
export default class StudyQuestionVertical extends Vue {
  // 問題切替中フラグ
  @Prop()
  isChangingProblem!: boolean

  // 問題番号
  @Prop()
  problemNumber!: number

  // 設問番号テキスト
  @Prop()
  questionNumberText!: string

  // 音声URL
  @Prop()
  audioUrl?: string

  // 音声再生回数
  @Prop()
  audioPlayCount?: number

  // 音声コメント画像URL
  @Prop()
  audioCommentImageUrl!: string

  // 音声表示設定
  @Prop()
  audioViewSetting!: AudioViewSetting

  // 問題画像URL群
  @Prop()
  imageUrls!: string[]

  // 音声再生時イベント
  @Prop()
  audioStartedEvent!: Function

  // 描画終了時イベント
  @Prop()
  drawEndEvent!: Function

  // 描画データ
  @Prop()
  questionDrawingData?: DrawingDataType | null

  // ペン選択のRef
  @Ref()
  penSelector!: PenSelector

  // ペンキャンバスのRef
  @Ref()
  penCanvas!: PenCanvas

  @Ref()
  questionBlock!: HTMLElement

  private handleResize = throttle(() => {
    if (this.penCanvas && this.questionBlock) {
      this.changeCanvasSize()
    }
  }, 100)

  private debouncedHandleResize = debounce(this.adjustAndRestoreCanvas, 50)

  private mounted(): void {
    window.addEventListener('resize', this.debouncedHandleResize)
  }

  private beforeDestroy() {
    window.removeEventListener('resize', this.debouncedHandleResize)
  }

  private activePen: DrawSettings.PenAsset | null = null

  private changeActivePen() {
    this.activePen = this.penSelector?.activePen || null
  }

  private adjustAndRestoreCanvas() {
    this.changeCanvasSize()
  }

  /**
   * isChangingProblemを監視して再描画
   * - immediate: trueにより、マウント時にも実行される
   * - isChangingがfalseになったタイミングで実行することで、
   *   propsの更新とDOMの反映が完了した状態でcanvasを初期化できる
   */
  @Watch('isChangingProblem', { immediate: true })
  private async onIsChangingProblemChanged(isChanging: boolean): Promise<void> {
    if (!isChanging) {
      await this.$nextTick()
      this.changeCanvasSize()
    }
  }

  private restoreDrawingData() {
    if (this.penCanvas) {
      // 既存の描画データがあれば復元、なければクリアする
      this.penCanvas.restoreDrawingData(this.questionDrawingData || null)
    }
  }

  private setCanvas(size: { width: number; height: number } | undefined): void {
    if (!this.penCanvas || !size) return

    this.penCanvas.canvas.setCanvasSize(size.width, size.height)
  }

  private calculateCanvasSize(element: HTMLElement | undefined): { width: number; height: number } | undefined {
    if (!element) return undefined
    return { width: element.clientWidth, height: element.clientHeight }
  }

  /**
   * canvasサイズ変更時、出題エリアサイズ変更時の処理
   * このメソッドがcanvasのサイズ設定と描画データ復元の両方を担当する
   */
  private changeCanvasSize(): void {
    if (!this.penCanvas || !this.questionBlock) {
      return
    }

    // canvasサイズ設定
    const newSize = this.calculateCanvasSize(this.questionBlock)
    this.setCanvas(newSize)

    // 描画データを復元（スケーリングもここで行われる）
    this.restoreDrawingData()
  }

  private get setStyle(): { [key: string]: number | string } {
    return {
      '--audio-padding': this.audioUrl ? '45px' : 'unset',
    }
  }
}
</script>

<style lang="stylus" scoped>
.study-question-vertical
  display flex
  flex-direction row-reverse
  border 2px solid var(--color-study-content__border)
  background-color var(--color-study-content__background)
  border-radius 4px
  padding 20px 10px

  &__title
    position relative
    display flex
    align-items center
    justify-content space-between
    color var(--color-study-content-title__text)
    background-color var(--color-study-content-title__background)
    margin-left 10px
    border-radius 10px
    padding 20px 2px
    writing-mode vertical-rl
    text-orientation upright
    &__text
      display flex
      &__problem-number
        display flex

  &__audio
    position relative
    display flex
    justify-content flex-start
    align-items flex-start
    flex-direction column
    padding-top var(--audio-padding)
    &__comment-image
      max-height 100%
      max-width unset
    &__player
      position absolute
      display flex
      z-index 1
      top 0
      right 10px

  &__content
    overflow-x scroll
    padding-top var(--audio-padding)
    writing-mode vertical-rl
    max-height 100%
    width 100%
    &__question
      position relative
      height 100%
      min-width 150vh
      &__image
        max-height 100%
        max-width unset
      &__canvas
        position absolute
        height 100%
        top 0
        left 0
</style>
